import { ActionIcon, Box, Button, Group, Modal, ScrollArea, Skeleton, Stack, Text, Tooltip } from "@mantine/core";
import {
  AudioLines,
  BadgeCheck,
  FileText,
  Image,
  type LucideIcon,
  Magnet,
  Package,
  UserRoundPlus,
  Video,
  Volume2,
  VolumeX,
  WandSparkles,
} from "lucide-react";
import { useState } from "react";

import { useListTemplates } from "../../api/generated/endpoints/generation/generation";
import type { TemplateCard as Template, TemplateNeed } from "../../api/generated/model";
import { freshUrls } from "../../api/signed-urls";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { LoopingVideo } from "../../ui/LoopingVideo";
import { TemplateCard } from "./TemplateCard";
import classes from "./templates.module.css";

/**
 * Live's "What you’ll need" rows (`constants/templates.tsx`): each kind's icon and label. A row the
 * template runs without (`optional`, live's `isOptional`) reads the same: live's `RequirementChip`
 * reads only the kind and the label (G14-catalog-2026-10-28).
 */
const NEEDS: Record<TemplateNeed, { label: string; Icon: LucideIcon }> = {
  video: { label: "Video", Icon: Video },
  image: { label: "Image", Icon: Image },
  product: { label: "Product", Icon: Package },
  actor: { label: "AI Actor", Icon: UserRoundPlus },
  voice: { label: "Voice", Icon: AudioLines },
  script: { label: "Script", Icon: FileText },
  logo: { label: "Brand logo", Icon: BadgeCheck },
  prompt: { label: "Prompt", Icon: WandSparkles },
  hooks: { label: "Hooks", Icon: Magnet },
};

/** Explore more reads every card of the medium at once, as live's: the route's largest page. */
const EVERY = 100;

export interface TemplateDialogProps {
  teamId: string;
  /** The template previewed; none while the dialog is closed. */
  template: Template | null;
  /** Explore more's card: the dialog shows that template instead. */
  onPreview: (template: Template) => void;
  /** Use (empty) or Replicate (the example's inputs): the form panel opens; the dialog closes. */
  onSelect: (template: Template, replicate: boolean) => void;
  onClose: () => void;
}

/**
 * Live's template preview (`template-preview-modal`, captures 03, 41 and 42): a 1024px dialog
 * named after the template; at the left its description, category, "What you’ll need", live's
 * note, Use and Replicate; at the right its examples; under them Explore more, the other
 * templates of its medium that have examples. It closes on the x, Esc and a click outside, and
 * gives focus back to the card.
 */
export function TemplateDialog({ teamId, template, onPreview, onSelect, onClose }: TemplateDialogProps) {
  return (
    <Modal
      opened={template !== null}
      onClose={onClose}
      title={template?.title}
      size={1024}
      radius={24}
      padding={20}
      centered
      classNames={{ content: classes.dialog, title: classes.dialogTitle, body: classes.dialogBody }}
    >
      {template === null ? null : (
        // A new template starts again: the first example, muted.
        <div key={template.id} className={classes.dialogContent}>
          <div className={classes.topRow}>
            <Details template={template} onSelect={(replicate) => onSelect(template, replicate)} />
            <Examples template={template} />
          </div>
          <ExploreMore teamId={teamId} template={template} onPreview={onPreview} />
        </div>
      )}
    </Modal>
  );
}

function Details({ template, onSelect }: { template: Template; onSelect: (replicate: boolean) => void }) {
  const { description, requirements } = template;
  return (
    <Stack gap={16} className={classes.rail}>
      {description === null ? null : <Text className={classes.description}>{description}</Text>}
      <span className={classes.categoryTag}>{template.category}</span>
      {requirements.length === 0 ? null : (
        <>
          <Text className={classes.sectionTitle}>What you’ll need</Text>
          <Stack component="ul" gap={8} className={classes.needs} aria-label="What you’ll need">
            {requirements.map((need) => {
              const { label, Icon } = NEEDS[need.kind];
              return (
                <li key={`${need.kind}:${need.label ?? ""}`} className={classes.need}>
                  <Icon size={16} strokeWidth={1.5} aria-hidden />
                  <span>{need.label ?? label}</span>
                </li>
              );
            })}
          </Stack>
        </>
      )}
      <Text className={classes.note}>
        Inputs stay in the existing composer after you replicate this template.
      </Text>
      <Group grow gap={8} wrap="nowrap" className={classes.actions}>
        <Button className={classes.action} onClick={() => onSelect(false)}>
          Use
        </Button>
        {/* A disabled button takes no pointer, so the tooltip hangs on a wrapper, as live's. */}
        <Tooltip
          label="This template has no example to replicate yet."
          disabled={template.has_example}
          multiline
        >
          <Box className={classes.actionSlot}>
            <Button
              variant="outlined-light"
              fullWidth
              className={classes.action}
              disabled={!template.has_example}
              onClick={() => onSelect(true)}
            >
              Replicate
            </Button>
          </Box>
        </Tooltip>
      </Group>
    </Stack>
  );
}

/**
 * Live's example panel: the chosen example large (a muted looping video with its sound button,
 * or a picture), the others beside it, each one click from taking its place. None: nothing.
 */
function Examples({ template }: { template: Template }) {
  const urls = template.example_urls;
  const [chosen, setChosen] = useState(0);
  const [sound, setSound] = useState(false);
  const shown = urls[chosen];
  if (shown === undefined) return null;
  const video = template.media === "video";
  const name = `${template.title} example`;
  return (
    <div className={classes.examples}>
      <div className={classes.primary} data-sound={sound || undefined}>
        {video ? (
          <LoopingVideo key={shown} muted={!sound} className={classes.exampleMedia} src={shown} aria-label={name} />
        ) : (
          <img className={classes.exampleMedia} src={shown} alt={name} />
        )}
        {video ? (
          <ActionIcon
            variant="transparent"
            className={classes.sound}
            aria-label={sound ? "Mute" : "Unmute"}
            onClick={(event) => {
              event.stopPropagation();
              event.currentTarget.blur();
              setSound(!sound);
            }}
          >
            {sound ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </ActionIcon>
        ) : null}
      </div>
      {urls.length < 2 ? null : (
        <ScrollArea type="hover" scrollbarSize={6} className={classes.thumbs}>
          <Stack gap={20} justify="center" className={classes.thumbsColumn}>
            {urls.map((url, at) =>
              at === chosen ? null : (
                <button
                  key={url}
                  type="button"
                  className={classes.thumb}
                  aria-label={`Show example ${at + 1}`}
                  onClick={() => {
                    setChosen(at);
                    // Another example starts muted, as autoplay asks.
                    setSound(false);
                  }}
                >
                  {video ? (
                    <LoopingVideo className={classes.exampleMedia} src={url} />
                  ) : (
                    <img className={classes.exampleMedia} src={url} alt="" />
                  )}
                </button>
              ),
            )}
          </Stack>
        </ScrollArea>
      )}
    </div>
  );
}

/**
 * Live's Explore more: every other template of the same medium that can open and has examples,
 * in the library's order, a 150 by 200 card each; a card shows its template here instead.
 */
function ExploreMore({
  teamId,
  template,
  onPreview,
}: {
  teamId: string;
  template: Template;
  onPreview: (template: Template) => void;
}) {
  const others = useListTemplates(
    teamId,
    { media: template.media, limit: EVERY },
    { query: freshUrls((page) => page.urls_expire_at) },
  );
  const cards = others.data?.items.filter(
    (each) => each.id !== template.id && !each.coming_soon && each.example_urls.length > 0,
  );
  return (
    <section className={classes.explore} aria-label="Explore more">
      <Text className={classes.sectionTitle}>Explore more</Text>
      {others.isError ? <ErrorAlert error={others.error} /> : null}
      {cards === undefined ? (
        others.isError ? null : (
          <Group gap={8} wrap="nowrap" className={classes.exploreRow}>
            {Array.from({ length: 6 }, (_, at) => (
              <Skeleton key={at} className={classes.exploreCard} radius={16} />
            ))}
          </Group>
        )
      ) : cards.length === 0 ? (
        <Text className={classes.exploreEmpty}>No other templates of this type yet.</Text>
      ) : (
        <ScrollArea type="hover" scrollbarSize={6}>
          <ul className={classes.exploreRow}>
            {cards.map((card) => (
              <li key={card.id} className={classes.exploreCard}>
                <TemplateCard template={card} onOpen={() => onPreview(card)} />
              </li>
            ))}
          </ul>
        </ScrollArea>
      )}
    </section>
  );
}
